import { MODULES, type ModuleId, type ModuleMeta, type OverrideMode } from '@tahi/core';
import { ChevronRight } from 'lucide-react';
import { useId } from 'react';
import { Link } from 'react-router';
import { overrideFor } from '../../data/access';
import { setModuleEnabled } from '../../data/commands/modules';
import { useCommandEnv, useMe, useModuleStates, useUsable } from '../../data/hooks';
import { getWebModule, modulePath } from '../../modules/registry';
import { Switch } from '../../ui/Switch';
import { linkNotes, MODULE_DESCRIPTIONS } from './moduleCopy';

const FOCUS =
  'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[var(--ring)]';

interface RowProps {
  meta: ModuleMeta;
  enabled: boolean;
  override: OverrideMode;
  available: boolean;
  /** On, usable and built: the name and description open the module's dashboard. */
  canOpen: boolean;
  onToggle(on: boolean): void;
}

function ModuleRow({ meta, enabled, override, available, canOpen, onToggle }: RowProps) {
  const descId = useId();
  let status: string | null = null;
  if (override === 'forced_on') status = 'Managed by admin · On';
  else if (override === 'forced_off') status = 'Managed by admin · Off';
  else if (meta.alwaysOn) status = 'Always on';
  else if (!available) status = 'Coming soon';
  const checked =
    override === 'forced_on' || (override !== 'forced_off' && (Boolean(meta.alwaysOn) || enabled));
  const text = (
    <div className="min-w-0 flex-1">
      <p className="font-medium">{meta.name}</p>
      <p id={descId} className="text-sm text-muted">
        {MODULE_DESCRIPTIONS[meta.id]}
        {status ? <span className="mt-1 block text-xs font-medium text-fg">{status}</span> : null}
      </p>
    </div>
  );
  return (
    <li className="flex items-center gap-1 rounded-[24px] border border-divider bg-surface-2 pr-3">
      {canOpen ? (
        <Link
          to={modulePath(meta.id)}
          aria-label={`Open ${meta.name}`}
          aria-describedby={descId}
          className={`flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-[24px] p-4 pr-1 hover:bg-secondary/40 ${FOCUS}`}
        >
          {text}
          <ChevronRight aria-hidden className="h-5 w-5 shrink-0 text-muted" />
        </Link>
      ) : (
        <div className="flex min-w-0 flex-1 p-4 pr-1">{text}</div>
      )}
      <Switch
        label={meta.name}
        checked={checked}
        disabled={status !== null}
        describedBy={descId}
        onCheckedChange={onToggle}
      />
    </li>
  );
}

export function ModulesScreen() {
  const env = useCommandEnv();
  const me = useMe();
  const usable = useUsable();
  const { data: states = [] } = useModuleStates();
  const rows = (Object.values(MODULES) as ModuleMeta[]).filter((m) => m.id !== 'today');
  const enabledById = new Map(states.map((s) => [s.moduleId as ModuleId, s.enabled]));
  const notes = linkNotes(usable);

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-[26px] font-bold leading-tight">Modules</h1>
        <p className="text-sm text-muted">
          Turn on only what you use. Turning one off hides it; nothing is deleted.
        </p>
      </header>
      <ul className="flex flex-col gap-2">
        {rows.map((m) => (
          <ModuleRow
            key={m.id}
            meta={m}
            enabled={enabledById.get(m.id) ?? false}
            override={overrideFor(me, m.id)}
            available={getWebModule(m.id) !== undefined}
            canOpen={getWebModule(m.id) !== undefined && usable(m.id)}
            onToggle={(on) => void setModuleEnabled(env, m.id, on)}
          />
        ))}
      </ul>
      {notes.length > 0 ? (
        <section aria-label="How your modules connect" className="flex flex-col gap-2">
          {notes.map((n) => (
            <p
              key={n}
              className="rounded-2xl border border-divider bg-surface p-3 text-sm text-muted"
            >
              {n}
            </p>
          ))}
        </section>
      ) : null}
    </div>
  );
}
